import { BehaviorSubject, filter, map, merge, timer } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { Component, DestroyRef, inject, InjectionToken, Injector, runInInjectionContext, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { CommonModule } from '@angular/common'
import { FormControl, FormGroup, FormRecord, FormsModule } from '@angular/forms'
import { NgbNav, NgbNavModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { windowLet } from 'rx-op-sliding-window'

import { UnfilteredTag } from 'elements-sdk'
import { APIService } from 'api'
import { fixedPrecision } from 'common/helpers'
import { LocaleModule } from 'common/locale'
import { MessageBoxService, UtilCommonModule } from 'common/util'
import { BadgeModule } from 'common/util.badge'
import { VersatileFieldsModule } from 'common/versatileFields'
import { AnyFieldDataType, createFormControl, createFormGroup, getValidationHintString, VersatileFieldDefinition, VersatileFieldValidationRule } from 'common/versatileFields/types'
import { MediaTagsInputComponent } from 'media.badges'
import { CustomFieldsService, MediaCustomFieldsModule, parseCustomField } from 'media.customFields'
import { File, UploaderService } from 'uploader/services/uploader.service'

export const MEASUREMENT_PERIOD = new InjectionToken<number>('MEASUREMENT_PERIOD')
export const TOTAL_MEASUREMENT_PERIOD = new InjectionToken<number>('TOTAL_MEASUREMENT_PERIOD')

@Component({
  selector: 'uploader-panel',
  templateUrl: './uploaderPanel.component.html',
  styleUrls: ['./uploaderPanel.component.scss'],
  imports: [
    CommonModule,
    UtilCommonModule,
    BadgeModule,
    FormsModule,
    LocaleModule,
    MediaCustomFieldsModule,
    MediaTagsInputComponent,
    NgbNavModule,
    NgbTooltipModule,
    VersatileFieldsModule,
    MediaCustomFieldsModule,
  ],
})
export class UploaderPanelComponent {
  readonly #measurementPeriod = inject(MEASUREMENT_PERIOD, { optional: true }) ?? 250
  readonly #totalMeasurementPeriod = inject(TOTAL_MEASUREMENT_PERIOD, { optional: true }) ?? 2000

  selectedFile: File | null = null

  readonly humanReadableUploadSpeed$ = timer(0, this.#measurementPeriod)
    .pipe(
      map(() => this.uploader.getUploadSpeed()),
      (windowLet as any)(this.#totalMeasurementPeriod),
      map((recentMeasurements: ReadonlyArray<number>) => {
        if (recentMeasurements.length === 0) {
          return null
        }
        const bytes = recentMeasurements.reduce((sum, measurement) => sum + measurement, 0) / recentMeasurements.length
        return Math.floor(bytes)
      }),
    )

  visible$ = new BehaviorSubject<boolean>(false)

  get visible (): boolean {
    return this.visible$.getValue()
  }

  set visible (value: boolean) {
    this.visible$.next(value)
  }

  @ViewChild(NgbNav) tabs?: NgbNav

  Math = Math

  constructor (
    private api: APIService,
    private messageBox: MessageBoxService,
    private translate: TranslateService,
    protected customFields: CustomFieldsService,
    private injector: Injector,
    public uploader: UploaderService,
    private destroyRef: DestroyRef,
  ) { }

  get uploaderFlowFiles () {
    return this.uploader.flow.files as Array<File>
  }

  fields: readonly {
    readonly name: string
    readonly definition: VersatileFieldDefinition
    readonly validationRule: VersatileFieldValidationRule
    readonly required: boolean
    readonly tooltip: string | null
  }[]

  form: FormGroup<{
    fallbacks: FormGroup<{
      customFields: FormGroup<Record<string, FormControl<AnyFieldDataType | null>>>
      tags: FormControl<readonly UnfilteredTag[]>
    }>
    overrides: FormRecord<FormGroup<{
      customFields: FormGroup<Record<string, FormControl<AnyFieldDataType | null>>>
      tags: FormControl<readonly UnfilteredTag[]>
    }>>
  }>

  get formValue () {
    return this.form.getRawValue()
  }

  createFields () {
    const newFields = this.uploader.currentConfig()?.mediaRoot.customFields
      .filter((customField) =>
        customField.useForUploads
        && !customField.nonUserEditable
        && (!!this.uploader.permission()?.allowWriteDb || customField.requireToUpload),
      )
      .map((customField) => {
        const outcome = parseCustomField(customField)
        if (outcome.errors != null) console.error(outcome.errors)
        const { definition, validationRule } = outcome.data
        const tooltip = (this.translate.instant(getValidationHintString(validationRule) ?? ' ', validationRule) + '\n' + (customField.helpText ?? '')).trim()
        return {
          name: customField.name,
          definition,
          validationRule,
          required: customField.requireToUpload,
          tooltip,
        }
      }) ?? []

    this.fields = newFields

    const customFieldsGroup = this.form.controls.fallbacks.controls.customFields as FormGroup

    // Only add missing fields, don't reset existing ones
    for (const field of newFields) {
      if (!customFieldsGroup.contains(field.name)) {
        customFieldsGroup.addControl(
          field.name,
          createFormControl(field.definition, field.validationRule),
        )
      }
    }
  }

  private initForm () {
    this.form = new FormGroup({
      fallbacks: new FormGroup({
        customFields: new FormGroup({}),
        tags: new FormControl([] as readonly UnfilteredTag[], { nonNullable: true }),
      }),
      overrides: new FormRecord({}),
    })
  }

  ngOnInit () {
    this.initForm()
    this.createFields()
    this._clear()

    let uploadedFiles: File[] = []
    merge(
      runInInjectionContext(this.injector, () => toObservable(this.uploader.currentConfig)),
      this.visible$.pipe(filter(visible => visible)), // update custom fields when uploader panel shows up
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(250)
      ).subscribe(() => {
        this.createFields()
      })

    this.uploader.filesAdded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((files) => {
        uploadedFiles.push(...files)
        for (const file of files) {
          this.form.controls.overrides.addControl(
            file.fullPath,
            new FormGroup({
              customFields: createFormGroup(this.fields, {
                validationRule: (field) => field.validationRule,
              }),
              tags: new FormControl([] as readonly UnfilteredTag[], { nonNullable: true }),
            }),
          )
        }

        if (!this.haveFields()) {
          this.startAll(files)
        } else {
          this.visible = true
          this.tabs?.select('fallbacks')
        }
      })

    this.uploader.fileRemoved$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(file => {
        uploadedFiles = uploadedFiles.filter(f => f.uniqueIdentifier !== file.uniqueIdentifier)
      })

    this.uploader.uploaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(file => {
        // handling same file upload
        const originalFilePath = `${file.path}/${file.file.name}`
        if (file.fullPath !== originalFilePath) {
          const oldControl = this.form.controls.overrides.controls[originalFilePath]!
          this.form.controls.overrides.removeControl(originalFilePath)
          this.form.controls.overrides.addControl(file.fullPath, oldControl)
        }
      })

    merge(
      this.uploader.uploaded$,
      this.uploader.fileRemoved$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.allFilesDone() && uploadedFiles.length) {
          this.api.mediaLibrary.webUploadCompleted({
            webUploadCompleted: {
              paths: uploadedFiles.filter(f => !f.error).map(f => f.fullPath),
            },
          })
          uploadedFiles = []
        }
      })

    this.uploader.complete$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.visible = false
      })
  }

  ngOnDestroy () {
    this.visible = false
  }

  private _clear () {
    this.selectedFile = null
    this.uploader.clear()
    this.form.reset()
    this.visible = false
  }

  async clear () {
    if ([this.uploader.state.UPLOADING, this.uploader.state.PAUSED].includes(this.uploader.getState())) {
      try {
        await this.messageBox.show({
          text: this.translate.instant(`Abort current uploads?`),
          positive: this.translate.instant(`Abort`),
          negative: this.translate.instant(`Don't abort`),
          danger: true,
        })
      } catch {
        return
      }
    }
    this._clear()
  }

  private twoDigits (num: number) {
    return fixedPrecision(num, 2, 'floor')
  }

  getFileProgress (file: File) {
    return this.twoDigits((file.sizeUploaded() / file.size) * 100)
  }

  getTotalProgress () {
    return this.twoDigits(this.uploader.getProgress() * 100)
  }

  selectFile (file: File) {
    // Before moving to a different form, we save the values from the currently visible form
    // to a local autocompletion pool.
    if (this.selectedFile) {
      for (const field of this.fields) {
        const fileMetadata = this.formValue.overrides[this.selectedFile.fullPath]?.customFields
        if (fileMetadata != null) {
          const value = fileMetadata[field.name]
          if (value != null) {
            this.customFields.registerAutocompletion({ name: field.name, type: field.definition.type }, value)
          }
        }
      }
    }
    this.selectedFile = file
    this.updateSelectedTab()
  }

  protected updateSelectedTab () {
    if (this.uploaderFlowFiles.length > 1) {
      this.tabs?.select('overrides')
    } else {
      this.tabs?.select('fallbacks')
    }
  }

  retry (file: File) {
    file.retry()
    this.setMetadata(file)
  }

  startFile (file: File) {
    file.resume()
    file.pending = false

    this.setMetadata(file)
  }

  private setMetadata (file: File) {
    const customFields = this.customFields.combine(
      this.formValue.fallbacks.customFields,
      this.formValue.overrides[file.fullPath]?.customFields,
    )
    let tags: UnfilteredTag[] = []
    if (this.uploader.permission()?.allowWriteDb) {
      tags = [
        ...this.formValue.fallbacks.tags,
        ...this.formValue.overrides[file.fullPath]?.tags ?? [],
      ]
    }

    this.api.main.registerUploadMetadata({
      registerUploadMetadataEndpointRequest: {
        items: [
          {
            uploadId: file.uniqueIdentifier,
            path: file.fullPath,
            customFields,
            tags: tags as any,
          },
        ],
      },
    })
    for (const field of this.fields) {
      const value = this.formValue.fallbacks.customFields[field.name]
      if (value) {
        this.customFields.registerAutocompletion({ type: field.definition.type, name: field.name }, value)
      }
    }
  }

  startAll (files?: File[]) {
    for (const file of files ?? this.uploaderFlowFiles) {
      if (this.haveNecessaryMetadataFor(file)) {
        if (file.paused) {
          this.startFile(file)
        }

        if (file.error) {
          this.retry(file)
        }
      }
    }
  }

  pause (file: File) {
    file.pause()
    ;(this.uploader.flow as any).fire('pause', file)
  }

  haveAllNecessaryMetadata () {
    if (!this.haveFields()) {
      return true
    }
    return this.uploaderFlowFiles.every(x => this.haveNecessaryMetadataFor(x))
  }

  haveNecessaryMetadataFor (file: File) {
    if (!this.haveFields()) return true
    return !this.fields.some((aField) => {
      return aField.required
        && this.formValue.fallbacks.customFields[aField.name] == null
        // There's a brief time span during which `uploader.flow.files` and the form are desynchronized.
        // The discrepancy originates from the delay introduced in `uploader.filesAdded$` due to
        // additional asynchronous work we before emitting files (see the `onFilesAdded` method in
        // `UploaderService`). The proper solution is to avoid using `uploader.flow.files` directly and
        // instead add our own layer on top of it, to allow consuming only the files which have gone
        // through our additional logic.
        && this.formValue.overrides[file.fullPath]?.customFields[aField.name] == null
    })
  }

  judgeMetadataValidity (file: File): 'valid' | 'invalid' {
    if (file.done) return 'valid'
    if (!this.haveNecessaryMetadataFor(file)) return 'invalid'
    if (!this.form.valid) return 'invalid'
    return 'valid'
  }

  haveFields () {
    return this.fields.length > 0
  }

  allFilesDone () {
    return this.uploaderFlowFiles.every((x: File) => x.done)
  }

  haveErrors () {
    return this.uploaderFlowFiles.some((x: File) => x.error)
  }

  getPausedUploadsCount () {
    return this.uploaderFlowFiles.filter((x: File) => x.paused && !x.done && !x.error).length
  }

  areSomeUploadsInProgress () {
    return this.uploader.getState() === this.uploader.state.UPLOADING && this.getPausedUploadsCount() > 0
  }

  areAllUploadsInProgress () {
    return this.uploader.getState() === this.uploader.state.UPLOADING && this.getPausedUploadsCount() === 0
  }

  /**
   * Check how many times a field was overridden on a per-file basis. We're not interested in the exact count,
   * but in one of the three disjunct categories.
   *
   * - `never` - The field's value is not overridden in any of the files.
   * - `always` - The field's value is overridden in all files.
   * - `sometimes` - The field's value is overridden in some files, but not all of them.
   *
   * This piece of information on its own isn't very useful, but it plays a key part in validating the form.
   *
   * @see usedAsFallback
   * @see analyzeRequirednessConstraintForFallback
   */
  getFallbackUsageCount (fieldName: string): 'never' | 'sometimes' | 'always' {
    const overrides = Object.values(this.formValue.overrides)
    const overriddenCount = overrides.reduce((count, { customFields }) => {
      return count + (customFields[fieldName] == null ? 0 : 1)
    }, 0)
    // If the fallback value is never overridden, it means it's always used.
    if (overriddenCount === 0) return 'always'
    // If the fallback value is always overridden, it means it's never used.
    if (overriddenCount === overrides.length) return 'never'
    return 'sometimes'
  }

  /**
   * Each field has one **fallback** value for all files, and one **override** per each file separately.
   * This function asks the following question: “How often is the specified fallback value used?”.
   * The answer is one of the following four disjunct categories.
   *
   * - `irrelevant` - The fallback value is not filled in at all, so it makes no sense to ask “how often”;
   *   or there is only one file being uploaded, so the concept of fallbacks and overrides is meaningless.
   * - `never` - The fallback value is filled in and there's more than one file being uploaded, but the
   *   fallback value is never used, because it's being overridden by each file individually.
   * - `sometimes` - The fallback value is filled in and there's more than one file being uploaded, and
   *   the overriden value is filled in for at least one of the individual files (but not all).
   * - `always` - The fallback value is filled in and there's more than one file being uploaded, and
   *   the override value isn't filled in for any of the files.
   */
  usedAsFallback (fieldName: string): 'irrelevant' | 'never' | 'sometimes' | 'always' {
    const fallbackValue = this.formValue.fallbacks.customFields[fieldName]
    if (fallbackValue == null) return 'irrelevant'
    const overrides = Object.values(this.formValue.overrides)
    if (overrides.length <= 1) return 'irrelevant'
    return this.getFallbackUsageCount(fieldName)
  }

  /**
   * Some custom fields are defined as "required for upload". This means that the user must fill in
   * this value for each new file that's uploaded. To do this, the user has to either fill in the
   * fallback value for such a field, or to fill in each file's field separately.
   *
   * To properly communicate whether or not the **fallback** form is valid (and _why_ it's valid or
   * invalid), we recognize three disjunct states that the fallback value can be in.
   *
   * - `irrelevant` - The field isn't required to be filled in at all. We don't draw attention to
   *   “requiredness” in this case at all.
   * - `violated` - The field is required and at least one file has no value filled in. This happens
   *   when no fallback value is given and not all individual override values are filled in.
   * - `satisfied` - The field is required and the fallback value is filled in. Maybe some of the
   *   individual file's overrides are also filled in, maybe none, maybe all. Doesn't matter because
   *   there's a fallback value and we're evaluating the state of the fallback form.
   * - `satisfied-manually` - The fiels is required, the fallback value is not filled in, but each
   *   individual file's override is filled in. This is the least probably case, but it's necessary
   *   to understand this about the form, so we don't wrongly report the form being invalid -- after
   *   all, a required field is not _directly_ filled in.
   *
   * @see analyzeRequirednessConstraint
   */
  analyzeRequirednessConstraintForFallback (fieldName: string): 'irrelevant' | 'satisfied-manually' | 'satisfied' | 'violated' {
    const field = this.fields.find((aField) => aField.name === fieldName)
    if (field == null || !field.required) return 'irrelevant'
    const fallbackValue = this.formValue.fallbacks.customFields[fieldName]
    const usedAsFallback = this.getFallbackUsageCount(fieldName)
    if (fallbackValue == null) {
      if (usedAsFallback === 'always') return 'violated'
      return 'satisfied-manually'
    } else {
      return 'satisfied'
    }
  }

  /**
   * Where's the field's value coming from for a specific file?
   *
   * - `none` - No fallback value and no override.
   * - `straightforward` - No fallback, only “override” given. (It doesn't really “override” anything in
   *   this case.)
   * - `fallback` - A fallback is present and there's no override filled in.
   * - `override` - A fallback is present, but it's overridden by a given value for the individual file.
   */
  getValueSource (fieldName: string, filename: string): 'none' | 'straightforward' | 'override' | 'fallback' {
    const fallbackValue = this.formValue.fallbacks.customFields[fieldName]
    const overrideValue = this.formValue.overrides[filename]?.customFields[fieldName] ?? null
    if (fallbackValue == null) return overrideValue == null ? 'none' : 'straightforward'
    return overrideValue == null ? 'fallback' : 'override'
  }

  /**
   * Similar to {@link analyzeRequirednessConstraintForFallback}, but concerns itself with a specific file.
   *
   * @see analyzeRequirednessConstraintForFallback
   */
  analyzeRequirednessConstraint (fieldName: string, filename: string): 'irrelevant' | 'satisfied' | 'violated' {
    const field = this.fields.find((aField) => aField.name === fieldName)
    if (field == null || !field.required) return 'irrelevant'
    const valueSource = this.getValueSource(fieldName, filename)
    return valueSource === 'none' ? 'violated' : 'satisfied'
  }
}
